import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import ActionMenu from '../common/ActionMenu';

const OrdersList = ({
  orders = [],
  loading = false,
  pagination = { page: 1, limit: 20, total: 0, pages: 0 },
  filters = {},
  onFilterChange,
  onPageChange,
  onViewOrder,
  onEditOrder,
  onDeleteOrder,
  onStatusChange,
  onRefresh,  // Dodane - odświeżenie listy
  getStatusColor,
  getStatusLabel
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [generatingLabel, setGeneratingLabel] = useState(null); // ID zamówienia generującego etykietę
  
  // Stany sortowania
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Funkcja sortowania
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Komponent ikony sortowania
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <span style={{ opacity: 0.3, marginLeft: '4px' }}>↕</span>;
    }
    return <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  // Posortowane zamówienia
  const sortedOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    
    return [...orders].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'id':
          aVal = parseInt(a.id || 0);
          bVal = parseInt(b.id || 0);
          break;
        case 'numer_zamowienia':
          aVal = (a.numer_zamowienia || '').toLowerCase();
          bVal = (b.numer_zamowienia || '').toLowerCase();
          break;
        case 'customer_name':
          aVal = (a.customer_name || a.klient_nazwa || '').toLowerCase();
          bVal = (b.customer_name || b.klient_nazwa || '').toLowerCase();
          break;
        case 'created_at':
          aVal = new Date(a.created_at || a.data_zamowienia || 0).getTime();
          bVal = new Date(b.created_at || b.data_zamowienia || 0).getTime();
          break;
        case 'status':
          aVal = (a.status || '').toLowerCase();
          bVal = (b.status || '').toLowerCase();
          break;
        case 'items_count':
          aVal = parseInt(a.items_count || a.pozycji || 0);
          bVal = parseInt(b.items_count || b.pozycji || 0);
          break;
        case 'total':
          aVal = parseFloat(a.total_amount || a.wartosc || 0);
          bVal = parseFloat(b.total_amount || b.wartosc || 0);
          break;
        default:
          aVal = '';
          bVal = '';
      }
      
      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal, 'pl');
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      
      if (sortDirection === 'asc') {
        return aVal - bVal;
      }
      return bVal - aVal;
    });
  }, [orders, sortColumn, sortDirection]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    onFilterChange(localFilters);
  };

  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    const emptyFilters = { status: '', search: '', customer_id: '', location_id: '', page: 1 };
    setLocalFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  // Generowanie etykiety InPost
  const handleGenerateLabel = async (order) => {
    if (!order.shipping_point) {
      toast.error('Brak wybranego paczkomatu dla tego zamówienia');
      return;
    }

    setGeneratingLabel(order.id);
    try {
      const response = await api.post('/shipping/shipments', {
          order_id: order.id,
          service: order.shipping_method || 'inpost_locker_standard',
          target_point: order.shipping_point
        });
      const data = response.data;

      if (data.success) {
        toast.success(`Etykieta wygenerowana! Numer przesyłki: ${data.data?.tracking_number || 'Brak'}`);
        if (onRefresh) onRefresh();
      } else {
        toast.error(`Błąd: ${data.message}`);
      }
    } catch (error) {
      console.error('Błąd generowania etykiety:', error);
      toast.error('Błąd generowania etykiety');
    } finally {
      setGeneratingLabel(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (!price) return '0,00 zł';
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(price);
  };

  return (
    <div style={{ 
      backgroundColor: 'var(--card-bg)', 
      borderRadius: '6px', 
      border: '1px solid var(--border)', 
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
    }}>
      {/* Filtry - styl kompaktowy POS */}
      <div style={{ 
        padding: '0.5rem 0.75rem', 
        borderBottom: '1px solid var(--border)', 
        backgroundColor: 'var(--bg)' 
      }}>
        <form onSubmit={handleFilterSubmit}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1', minWidth: '150px' }}>
              <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>
                🔍 Szukaj
              </label>
              <input
                type="text"
                placeholder="Numer, klient..."
                value={localFilters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text)'
                }}
              />
            </div>
            
            <div style={{ minWidth: '120px' }}>
              <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>
                🏷️ Status
              </label>
              <select
                value={localFilters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text)'
                }}
              >
                <option value="">Wszystkie</option>
                <option value="pending">Oczekujące</option>
                <option value="processing">W realizacji</option>
                <option value="completed">Zrealizowane</option>
                <option value="cancelled">Anulowane</option>
              </select>
            </div>

            <div style={{ minWidth: '120px' }}>
              <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>
                📍 Lokalizacja
              </label>
              <select
                value={localFilters.location_id || ''}
                onChange={(e) => handleFilterChange('location_id', e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text)'
                }}
              >
                <option value="">Wszystkie</option>
                <option value="1">Główna</option>
                <option value="2">Filia 1</option>
                <option value="3">Filia 2</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '4px' }}>
              <button 
                type="submit" 
                disabled={loading}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  border: '1px solid var(--primary)', color: 'var(--primary)' ,
                  opacity: loading ? 0.7 : 1
                }}
              >
                🔍 Filtruj
              </button>
              <button 
                type="button" 
                onClick={clearFilters}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  border: '1px solid var(--border)',
                  cursor: 'pointer',
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text-secondary)'
                }}
              >
                ✕ Wyczyść
              </button>
            </div>

            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: 'auto' }}>
              Znaleziono: <strong>{pagination.total}</strong> zamówień
            </div>
          </div>
        </form>
      </div>

      {/* Tabela zamówień - styl kompaktowy POS */}
      <div style={{ overflowX: 'auto' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <p style={{ margin: 0, fontSize: '11px' }}>⏳ Ładowanie zamówień...</p>
          </div>
        ) : orders.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📭</div>
            <p style={{ margin: 0, fontSize: '11px', fontWeight: '600' }}>Brak zamówień</p>
            <p style={{ fontSize: '10px', margin: '0.3rem 0 0 0', color: 'var(--text-muted)' }}>
              Nie znaleziono zamówień spełniających kryteria
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
            <thead style={{ backgroundColor: 'var(--border)', position: 'sticky', top: 0 }}>
              <tr>
                <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none', width: '40px' }} onClick={() => handleSort('id')}>
                  LP<SortIcon column="id" />
                </th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('numer_zamowienia')}>
                  # Numer<SortIcon column="numer_zamowienia" />
                </th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('customer_name')}>
                  👤 Klient<SortIcon column="customer_name" />
                </th>
                <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('created_at')}>
                  📅 Data<SortIcon column="created_at" />
                </th>
                <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>
                  🏷️ Status<SortIcon column="status" />
                </th>
                <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                  🧾 Paragon
                </th>
                <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('items_count')}>
                  📦 Poz.<SortIcon column="items_count" />
                </th>
                <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('total')}>
                  💰 Wartość<SortIcon column="total" />
                </th>
                <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: '10px', fontWeight: '600', borderBottom: '1px solid var(--border)' }}>
                  ⚙️ Akcje
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map((order, index) => (
                <tr key={order.id} style={{ backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                  <td style={{ padding: '5px 8px', fontSize: '10px', borderBottom: '1px solid var(--border)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    {index + 1}
                  </td>
                  <td style={{ padding: '5px 8px', fontSize: '10px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontWeight: '600', color: 'var(--text)' }}>
                      {order.numer_zamowienia}
                    </div>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>ID: {order.id}</div>
                  </td>
                  
                  <td style={{ padding: '5px 8px', fontSize: '10px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontWeight: '500' }}>
                      {order.nazwa_firmy || `${order.imie || ''} ${order.nazwisko || ''}`.trim() || 'Nieznany klient'}
                    </div>
                    {order.telefon && (
                      <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                        📞 {order.telefon}
                      </div>
                    )}
                  </td>
                  
                  <td style={{ padding: '5px 8px', fontSize: '10px', borderBottom: '1px solid var(--border)' }}>
                    <div>{formatDate(order.data_zamowienia)}</div>
                    {order.typ_realizacji && (
                      <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                        {order.typ_realizacji === 'do_odbioru' && '🏪 Odbiór'}
                        {order.typ_realizacji === 'dostawa' && '🚚 Dostawa'}
                        {order.typ_realizacji === 'na_miejscu' && '🍽️ Na miejscu'}
                      </div>
                    )}
                  </td>
                  
                  <td style={{ padding: '5px 8px', fontSize: '10px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                    <select
                      value={order.status}
                      onChange={(e) => onStatusChange(order.id, e.target.value)}
                      style={{
                        padding: '3px 6px',
                        fontSize: '9px',
                        fontWeight: '600',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        backgroundColor: getStatusColor(order.status),
                        color: 'white'
                      }}
                    >
                      <option value="pending">Oczekujące</option>
                      <option value="processing">W realizacji</option>
                      <option value="completed">Zrealizowane</option>
                      <option value="cancelled">Anulowane</option>
                    </select>
                  </td>

                  {/* Kolumna Paragon */}
                  <td style={{ padding: '5px 8px', fontSize: '10px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                    {order.numer_paragonu ? (
                      <span style={{
                        padding: '2px 6px',
                        backgroundColor: 'var(--success-light)',
                        color: 'var(--success)',
                        borderRadius: '4px',
                        fontSize: '9px',
                        fontWeight: '600'
                      }}>
                        🧾 {order.numer_paragonu}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '9px' }}>-</span>
                    )}
                  </td>

                  <td style={{ padding: '5px 8px', fontSize: '10px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                    <span style={{
                      padding: '2px 6px',
                      backgroundColor: 'var(--info-light)',
                      color: 'var(--info)',
                      borderRadius: '10px',
                      fontSize: '10px',
                      fontWeight: '600'
                    }}>
                      {order.liczba_pozycji || 0}
                    </span>
                  </td>
                  
                  <td style={{ padding: '5px 8px', fontSize: '10px', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontWeight: '600', color: 'var(--success)' }}>
                      {formatPrice(order.wartosc_brutto)}
                    </div>
                    <div style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                      netto: {formatPrice(order.wartosc_netto)}
                    </div>
                  </td>
                  
                  <td style={{ padding: '5px 8px', fontSize: '10px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                      {/* Info o paczkomacie */}
                      {order.typ_realizacji === 'dostawa' && order.shipping_point && (
                        <span
                          title={`Paczkomat: ${order.shipping_point}`}
                          style={{
                            padding: '3px 6px',
                            border: '1px solid var(--info)', color: 'var(--info)',
                            borderRadius: '3px',
                            fontSize: '8px',
                            fontWeight: 'bold'
                          }}
                        >
                          📦 {order.shipping_point}
                        </span>
                      )}
                      <ActionMenu actions={[
                        { icon: '👁️', label: 'Szczegóły', color: 'var(--info)', onClick: () => onViewOrder(order) },
                        order.typ_realizacji === 'dostawa' && order.shipping_point && {
                          icon: order.shipping_tracking ? '📦' : '🏷️',
                          label: order.shipping_tracking ? `Tracking: ${order.shipping_tracking}` : 'Etykieta InPost',
                          color: order.shipping_tracking ? 'var(--success)' : 'var(--orange)',
                          disabled: generatingLabel === order.id,
                          onClick: () => handleGenerateLabel(order)
                        },
                        { icon: '✏️', label: 'Edytuj', color: 'var(--warning)', disabled: order.status === 'completed', onClick: () => onEditOrder(order) },
                        { icon: '🗑️', label: 'Usuń', color: 'var(--danger)', disabled: order.status === 'completed', onClick: () => onDeleteOrder(order.id) },
                      ]} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginacja - styl kompaktowy POS */}
      {pagination.pages > 1 && (
        <div style={{ 
          padding: '0.5rem 0.75rem', 
          borderTop: '1px solid var(--border)', 
          backgroundColor: 'var(--bg)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
            Strona {pagination.page} z {pagination.pages} ({pagination.total} zamówień)
          </div>
          
          <div style={{ display: 'flex', gap: '3px' }}>
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid var(--border)',
                backgroundColor: pagination.page <= 1 ? 'var(--border)' : 'var(--card-bg)' ,
                color: pagination.page <= 1 ? 'var(--text-muted)' : 'var(--text-secondary)',
                cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
              }}
            >
              ◀
            </button>
            
            {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
              const page = Math.max(1, pagination.page - 2) + i;
              if (page > pagination.pages) return null;
              
              return (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  style={{
                    padding: '4px 10px',
                    fontSize: '11px',
                    border: page === pagination.page ? 'none' : '1px solid var(--border)',
                    backgroundColor: page === pagination.page ? 'var(--primary)' : 'var(--card-bg)' ,
                    color: page === pagination.page ? '#fff' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontWeight: page === pagination.page ? '600' : '500'
                  }}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid var(--border)',
                backgroundColor: pagination.page >= pagination.pages ? 'var(--border)' : 'var(--card-bg)' ,
                color: pagination.page >= pagination.pages ? 'var(--text-muted)' : 'var(--text-secondary)',
                cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer'
              }}
            >
              ▶
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersList;
